import { InfoCircledIcon, TrashIcon } from '@radix-ui/react-icons';
import { Code, Flex, IconButton, Table, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonCallout } from 'components/common/callouts';
import { SuperAdminIcon } from 'components/common/custom-icon/shorthands';
import { CommonDialog } from 'components/common/dialogs';
import { CommonTextInput } from 'components/common/form/text';
import { MachineContext } from 'contexts/machine.context';
import { lightFormat } from 'date-fns';
import { t } from 'i18next';
import { IBaseDialog } from 'interfaces/i-dialogs';
import { UserRole } from 'lib_ts/enums/auth.enums';
import {
  LOCAL_DATETIME_FORMAT,
  LOCAL_DATETIME_FORMAT_LONG,
} from 'lib_ts/enums/datetime.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { UserMsgType } from 'lib_ts/enums/sockets/users';
import {
  CodeMessage,
  LiveboardCodeCheckResponse,
  LiveboardQueryResponse,
  LiveboardRevokeRequest,
  LiveboardRevokeResponse,
} from 'lib_ts/zod/liveboard';
import { FC, useCallback, useContext, useEffect, useState } from 'react';
import { sendSocket } from 'services/socket';
import { useAuthStore } from 'stores/use-auth-store';

const LiveboardTable = () => {
  const wsConnected = useAuthStore((s) => s.wsConnected);
  const [liveSessions, setLiveSessions] = useState<LiveboardQueryResponse>();
  const [lastQueried, setLastQueried] = useState<Date>();

  const getLiveSessions = useCallback(() => {
    sendSocket<undefined, (response: LiveboardQueryResponse) => void>(
      UserMsgType.U2WS_LiveboardQuery,
      undefined, // no need to send any payload
      'LiveboardTable',
      undefined,
      (response: LiveboardQueryResponse) => {
        setLiveSessions(response);
        setLastQueried(new Date());
      }
    );
  }, []);

  useEffect(() => {
    if (!wsConnected) {
      return;
    }

    // ask the server to tell us who is live
    getLiveSessions();

    // re-ask every 5s
    const interval = setInterval(() => {
      getLiveSessions();
    }, 5_000);

    return () => {
      clearInterval(interval);
    };
  }, [wsConnected, getLiveSessions]);

  if (!lastQueried) {
    return null;
  }

  const hasActiveLiveSessions = liveSessions && liveSessions.sockets.length > 0;

  return (
    <Flex direction="column" gap={RADIX.FLEX.GAP.SM} mt="2">
      <Flex direction="row" gap={RADIX.FLEX.GAP.SM} align="center">
        <Text size={RADIX.TEXT.SIZE.SM} weight="medium">
          Active Live Monitors
        </Text>
        <SuperAdminIcon />
      </Flex>

      {hasActiveLiveSessions ? (
        <Table.Root>
          <Table.Row>
            <Table.ColumnHeaderCell>Socket ID</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Created</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Revoke</Table.ColumnHeaderCell>
          </Table.Row>
          {liveSessions.sockets.map((m) => (
            <Table.Row key={m.socketID}>
              <Table.Cell>
                <Code>{m.socketID}</Code>
              </Table.Cell>
              <Table.Cell>
                {lightFormat(new Date(m.created), LOCAL_DATETIME_FORMAT)}
              </Table.Cell>
              <Table.Cell align="center">
                <IconButton
                  size="1"
                  variant={RADIX.BUTTON.VARIANT.SECONDARY}
                  color={RADIX.COLOR.DANGER}
                  onClick={() => {
                    const msg: LiveboardRevokeRequest = {
                      socketID: m.socketID,
                    };

                    sendSocket<
                      LiveboardRevokeRequest,
                      (response: LiveboardRevokeResponse) => void
                    >(
                      UserMsgType.U2WS_LiveboardRevoke,
                      msg,
                      'LiveboardTable',
                      undefined,
                      () => {
                        // re-ask the server who is connected after revoking
                        getLiveSessions();
                      }
                    );
                  }}
                >
                  <TrashIcon />
                </IconButton>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Root>
      ) : (
        <CommonCallout
          icon={<InfoCircledIcon />}
          color={RADIX.COLOR.NEUTRAL}
          content="No active Live Monitor sessions"
        />
      )}

      <Flex justify="end">
        <Text size={RADIX.TEXT.SIZE.XS} color={RADIX.COLOR.SECONDARY}>
          Last Updated: {lightFormat(lastQueried, LOCAL_DATETIME_FORMAT_LONG)}
        </Text>
      </Flex>
    </Flex>
  );
};

export const ConnectLiveboardDialog: FC<IBaseDialog> = (props) => {
  const [linkCode, setLinkCode] = useState('');
  const { machine } = useContext(MachineContext);
  const current = useAuthStore((s) => s.current);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const onChange = (v: string | undefined) => {
    if (v === undefined || v.length > 6) {
      return;
    }

    setLinkCode(v);
    setError('');

    if (v.length < 6) {
      return;
    }

    // autosubmit code if the code is 6 characters
    setLoading(true);

    sendSocket<CodeMessage, (response: LiveboardCodeCheckResponse) => void>(
      UserMsgType.U2WS_LiveboardCodeCheck,
      { code: v },
      'LinkCodeDialog',
      undefined,
      (response: LiveboardCodeCheckResponse) => {
        setLoading(false);
        if (!response.success) {
          setError(t('settings.live-monitor-invalid-code').toString());
          return;
        }

        NotifyHelper.success({
          message_md: t('settings.live-monitor-connected'),
        });
        setError('');
        setLinkCode('');

        props.onClose();
      }
    );
  };

  return (
    <CommonDialog
      identifier={props.identifier}
      width={RADIX.DIALOG.WIDTH.SM}
      title={t('settings.connect-live-monitor')}
      content={
        <Flex direction="column" gap={RADIX.FLEX.GAP.FORM}>
          <Text size={RADIX.TEXT.SIZE.SM}>
            {t('settings.connect-live-monitor-msg', {
              machineID: machine.machineID,
            })}
          </Text>
          <CommonTextInput
            id="link-code"
            value={linkCode}
            onChange={onChange}
            disabled={loading}
            placeholder="e.g. 123456"
            monospace
          />

          {error && (
            <CommonCallout
              icon={<InfoCircledIcon />}
              color={RADIX.COLOR.DANGER}
              content={error}
            />
          )}

          {current.role === UserRole.admin && <LiveboardTable />}
        </Flex>
      }
      onClose={props.onClose}
    />
  );
};
